import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Panel, Stat } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, ago, label, errMsg } from "@/lib/api";

const n = (v) => (v ?? 0).toLocaleString();
const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
const countryName = (c) => { try { return c === "unknown" ? "Not stated" : regionNames.of(c); } catch { return c; } };
const titleCase = (s) => label(s).replace(/^./, (c) => c.toUpperCase());

const Dist = ({ title, rows, testid, fmt = titleCase }) => (
  <Panel testid={testid}>
    <h3 className="mb-3 font-semibold">{title}</h3>
    {rows.map((r) => (
      <div key={r.key} className="flex justify-between border-b py-1.5 text-sm last:border-0"><span>{fmt(r.key)}</span><span className="font-mono">{n(r.count)}</span></div>
    ))}
  </Panel>
);

const ProposeForm = ({ onDone }) => {
  const [f, setF] = useState({ kind: "greenhouse", board: "", employer: "", industry: "", website: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/admin/sources", f);
      toast.success(`Board found with ${r.data.probe_jobs} jobs. Added to the review queue.`);
      setF({ ...f, board: "", employer: "", industry: "", website: "" });
      onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-2 md:grid-cols-6" data-testid="source-propose-form">
      <select className="h-9 rounded-md border px-2 text-sm" value={f.kind} onChange={set("kind")} data-testid="source-kind-select">
        <option value="greenhouse">Greenhouse</option><option value="lever">Lever</option><option value="ashby">Ashby</option>
      </select>
      <Input placeholder="Board name (e.g. stripe)" value={f.board} onChange={set("board")} required data-testid="source-board-input" />
      <Input placeholder="Employer name" value={f.employer} onChange={set("employer")} required data-testid="source-employer-input" />
      <Input placeholder="Industry" value={f.industry} onChange={set("industry")} data-testid="source-industry-input" />
      <Input placeholder="Official website" value={f.website} onChange={set("website")} data-testid="source-website-input" />
      <Button type="submit" disabled={busy} data-testid="source-propose-button">{busy ? "Checking…" : "Check & queue"}</Button>
    </form>
  );
};

const SourceRow = ({ s, onDecide }) => (
  <tr className="border-b text-sm last:border-0" data-testid={`source-row-${s.source_key}`}>
    <td className="py-2 pr-3"><p className="font-medium">{s.employer}</p><p className="text-xs text-muted-foreground">{s.ats} · {label(s.category)}</p></td>
    <td className="py-2 pr-3 capitalize">{label(s.authorization_status)}{!s.enabled && s.authorization_status === "approved" && " (disabled)"}</td>
    <td className="py-2 pr-3 font-mono">{s.authorization_status === "pending_review" ? `~${n(s.probe_jobs)}` : n(s.active_jobs)}</td>
    <td className="py-2 pr-3 text-xs">{s.last_success_at ? ago(s.last_success_at) : "never"}{s.last_run && !s.last_run.ok && !s.last_run.skipped && <span className="ml-1 text-red-600">failed</span>}</td>
    <td className="max-w-[260px] py-2 pr-3 text-xs text-muted-foreground">{s.last_error || (s.last_run?.ok ? `${n(s.last_run.new)} new · ${n(s.last_run.closed)} closed · ${s.last_run.pages} req` : "")}</td>
    <td className="py-2 text-right">
      {s.authorization_status === "pending_review" ? (
        <span className="space-x-1"><Button size="sm" onClick={() => onDecide(s, "approve")} data-testid={`source-approve-${s.source_key}`}>Approve</Button>
          <Button size="sm" variant="outline" onClick={() => onDecide(s, "reject")} data-testid={`source-reject-${s.source_key}`}>Reject</Button></span>
      ) : s.authorization_status === "approved" && (
        <Button size="sm" variant="outline" onClick={() => onDecide(s, s.enabled ? "disable" : "enable")} data-testid={`source-toggle-${s.source_key}`}>{s.enabled ? "Disable" : "Enable"}</Button>
      )}
    </td>
  </tr>
);

export default function AdminInventory() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/inventory").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const decide = async (s, decision) => {
    try { await api.post(`/admin/sources/${encodeURIComponent(s.source_key)}/decision`, { decision }); toast.success(`${s.employer}: ${decision}d`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return <p className="text-muted-foreground">Loading…</p>;
  const v = d.verified, p = d.parsed;
  const pending = d.sources.filter((s) => s.authorization_status === "pending_review");
  return (
    <div className="space-y-6" data-testid="admin-inventory">
      <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Measured from the database</p>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 [&>*]:min-w-0">
        <Stat labelText="Searchable active" value={n(v.searchable_active)} testid="inv-searchable" hint={`${n(v.verified_active)} link-verified`} />
        <Stat labelText="New in 24h" value={n(v.new_24h)} testid="inv-new" hint={`${n(v.closed_24h)} closed in 24h`} />
        <Stat labelText="Unique employers" value={n(v.unique_employers)} testid="inv-employers" hint={`${v.employer_direct_pct}% of jobs employer-direct or government`} />
        <Stat labelText="Salary disclosed" value={`${v.salary_disclosed_pct}%`} testid="inv-salary" hint={`${n(v.salary_disclosed)} jobs, annual USD`} />
        <Stat labelText="Total discovered" value={n(v.total_discovered)} testid="inv-total" hint={`${n(v.closed_or_expired)} closed or expired (kept for audit)`} />
        <Stat labelText="Inconclusive links" value={n(v.link_status.inconclusive)} testid="inv-inconclusive" hint={`${n(v.link_status.broken)} broken · ${n(v.link_status.awaiting)} awaiting check`} />
        <Stat labelText="Duplicates hidden" value={n(v.duplicates_hidden)} testid="inv-dups" />
        <Stat labelText="Syncs (24h)" value={`${v.sync_runs_24h - v.sync_failures_24h}/${v.sync_runs_24h}`} testid="inv-syncs" hint={`${v.ingest_requests_24h} requests · ${v.ingest_mb_24h} MB · $0 data cost`} />
      </div>
      <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Parsed from listing text (estimates)</p>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 [&>*]:min-w-0">
        <Dist title={`Country (US ${p.us_pct}%)`} rows={p.by_country} testid="inv-country" fmt={countryName} />
        <Dist title="Seniority" rows={p.seniority} testid="inv-seniority" />
        <Dist title="Employment type" rows={p.employment_type} testid="inv-employment" />
      </div>
      <p className="text-xs text-muted-foreground" data-testid="inv-not-tracked">{p.note} Not tracked yet: {d.not_tracked.join("; ")}. Applications: {n(v.applications)} total.</p>
      <Panel testid="inv-review-queue">
        <h3 className="mb-1 font-semibold">Add an employer source</h3>
        <p className="mb-3 text-xs text-muted-foreground">Only public, documented job-board APIs. New sources wait for approval before anything is imported.{!d.usajobs_ready && " USAJOBS is waiting for its API key."}</p>
        <ProposeForm onDone={load} />
        {pending.length > 0 && <p className="mt-4 text-sm font-medium">{pending.length} waiting for review</p>}
      </Panel>
      <Panel testid="inv-sources">
        <h3 className="mb-3 font-semibold">Source registry ({d.sources.length})</h3>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left">
            <thead><tr className="border-b text-xs uppercase text-muted-foreground"><th className="py-2">Employer / platform</th><th>Status</th><th>Jobs</th><th>Last sync</th><th>Last result</th><th /></tr></thead>
            <tbody>{d.sources.sort((a, b) => (a.authorization_status === "pending_review" ? -1 : 0) - (b.authorization_status === "pending_review" ? -1 : 0) || b.active_jobs - a.active_jobs)
              .map((s) => <SourceRow key={s.source_key} s={s} onDecide={decide} />)}</tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
